import React, { useEffect, useState } from "react";
import styles from "./TextViewer.module.css";
import { TextViewerProps } from "./TextViewer.types";
import { fetchTextFile } from "./TextViewer.service";
import { ThreeCircles } from "react-loader-spinner";

/**
 * TextViewer Component
 * Displays plain-text files (.txt, .log, .csv, .json, .xml, etc.) in a styled, read-only viewer.
 */
const TextViewer: React.FC<TextViewerProps> = ({ preview }) => {
  const [fileContent, setFileContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let loaderTimeout: NodeJS.Timeout;
    if (loading) {
      loaderTimeout = setTimeout(() => setShowLoader(true), 300);
    } else {
      setShowLoader(false);
    }
    return () => clearTimeout(loaderTimeout);
  }, [loading]);

  useEffect(() => {
    const loadTextFile = async () => {
      if (!preview?.url) return;
      try {
        setLoading(true);
        setError(null);
        const text = await fetchTextFile(preview.url);
        setFileContent(text);
      } catch (err) {
        console.error("Error loading text file:", err);
        setError("Failed to load text file. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadTextFile();
  }, [preview?.url]);

  return (
    <div className={styles.textViewerWrapper}>
      {preview?.url ? (
        <>
          <div className={styles.toolbar}>
            <div className={styles.fileTitle}>{preview?.name}</div>
          </div>

          {showLoader && (
            <div className={styles.customGridLoader}>
              <ThreeCircles
                height="50"
                width="50"
                color="#4fa94d"
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
              <div className={styles.customGridLoaderText}>
                Loading your text file
              </div>
            </div>
          )}

          {!loading && !error && (
            <div className={styles.textContainer}>
              <pre className={styles.textContent}>{fileContent}</pre>
            </div>
          )}

          {error && <div className={styles.errorMessage}>{error}</div>}
        </>
      ) : (
        <span className={styles.previewFileMessage}>
          Please select a text file to preview.
        </span>
      )}
    </div>
  );
};

export default TextViewer;
